import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ProjectReportService } from '../../services/project-report.service';
import {
  PROJECT_REPORT_ACCEPTED_EXTENSIONS,
  PROJECT_REPORT_MAX_SIZE_BYTES,
  ProjectReport,
  projectReportFileIcon,
} from '../../models/project-report.model';

@Component({
  selector: 'app-modal-project-report',
  templateUrl: './modal-project-report.component.html',
  styleUrls: ['./modal-project-report.component.scss'],
  standalone: false,
})
export class ModalProjectReportComponent {
  readonly acceptedExtensions = PROJECT_REPORT_ACCEPTED_EXTENSIONS;
  readonly acceptAttribute = PROJECT_REPORT_ACCEPTED_EXTENSIONS.map((extension) => '.' + extension).join(',');
  readonly projectReportFileIcon = projectReportFileIcon;

  title = '';
  notes = '';
  file: File | null = null;
  fileError = '';
  saving = false;
  dragging = false;

  constructor(
    public dialogRef: MatDialogRef<ModalProjectReportComponent>,
    private projectReportService: ProjectReportService,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: { projectId: number | string; report?: ProjectReport },
  ) {
    if (data.report) {
      this.title = data.report.title;
      this.notes = data.report.notes ?? '';
    }
  }

  get isEdit(): boolean {
    return !!this.data.report;
  }

  get canSave(): boolean {
    const hasFile = this.isEdit || !!this.file;
    return hasFile && !this.fileError && !this.saving && (this.isEdit ? this.title.trim().length > 0 : true);
  }

  get currentFileName(): string {
    return this.file?.name ?? this.data.report?.file_name ?? '';
  }

  onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectFile(input.files?.[0] ?? null);
    input.value = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
    this.selectFile(event.dataTransfer?.files?.[0] ?? null);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging = true;
  }

  onDragLeave(): void {
    this.dragging = false;
  }

  clearFile(): void {
    this.file = null;
    this.fileError = '';
  }

  save(): void {
    if (!this.canSave) {
      return;
    }

    this.saving = true;
    const done = () => {
      this.saving = false;
      this.dialogRef.close(true);
    };
    const fail = () => {
      this.saving = false;
    };

    if (this.isEdit) {
      this.projectReportService.update(
        this.data.projectId,
        this.data.report.id,
        { title: this.title.trim(), notes: this.notes.trim(), file: this.file },
        done,
        fail,
      );
      return;
    }

    this.projectReportService.create(
      this.data.projectId,
      { title: this.title.trim(), notes: this.notes.trim(), file: this.file },
      done,
      fail,
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private selectFile(file: File | null): void {
    if (!file) {
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!this.acceptedExtensions.includes(extension)) {
      this.file = null;
      this.fileError = this.translateService.instant('modals.project-report.error-type', {
        types: this.acceptedExtensions.join(', '),
      });
      return;
    }

    if (file.size > PROJECT_REPORT_MAX_SIZE_BYTES) {
      this.file = null;
      this.fileError = this.translateService.instant('modals.project-report.error-size', {
        size: PROJECT_REPORT_MAX_SIZE_BYTES / (1024 * 1024),
      });
      return;
    }

    this.fileError = '';
    this.file = file;
    if (!this.title.trim()) {
      this.title = file.name.replace(/\.[^.]+$/, '');
    }
  }
}
